import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { isSafari } from 'react-device-detect';
import onlyUpdateForKeys from 'recompose/onlyUpdateForKeys';
import flow from 'lodash/fp/flow';
import { isEmpty, isNumber } from 'lodash';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { WorkbookState } from 'types';
import CommonTranslationVariant from 'translations/variants/common';
import { useFullScreen } from 'state/providers/FullScreenProvider';
import { useZoom, DEFAULT_ZOOM_SCALE } from 'state/providers/ZoomProvider';
import { translate } from 'translations/translator';
import { getPlatform } from 'util/user-agent-util';

const getTranslation = (key: CommonTranslationVariant) => translate('common', key);
const loadPdfWorker = version => {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
};

type Props = {
  workbook: WorkbookState;
  currentPage: number;
  fingerprint?: string | null;
  isTwoPages?: boolean;
};

const PdfViewer: any = ({ workbook, currentPage, isTwoPages = false }: Props) => {
  const navigate = useNavigate();
  const params = useParams();

  const platform = getPlatform();
  const isIos = platform.ios;
  const renderedDocument = document.getElementsByClassName('react-pdf__Document')?.[0] as any;
  const renderedCanvas = Array.from(
    document.getElementsByClassName('react-pdf__Page__canvas')
  ) as any;
  const renderedWorkbook = document.getElementById('workbook') as any;

  const [documentHeight, setDocumentHeight] = useState<number>(renderedDocument?.clientHeight);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [{ isFullScreen }] = useFullScreen();
  const [{ zoomScale }] = useZoom();
  const [aspectRatio, setAspectRatio] = useState('1/1');

  const isZoomedOut = zoomScale < DEFAULT_ZOOM_SCALE;
  const isZoomedIn = zoomScale > DEFAULT_ZOOM_SCALE;
  const isPortrait = workbook.orientation === 'portrait';
    const pageWidth = () => {
    const singlePage = !isTwoPages;
    const landscape = !isPortrait;

    if (isSafari || isIos) {
      if (singlePage && isPortrait) return isIos ? '40%' : '100%';
      if (isIos && isTwoPages && isPortrait) return '40%';
      if (singlePage && landscape) return '60%';
      return '50%';
    }

    return 'unset';
  };


  const { documentContainer, pageWrapper, container, pageContainer, left, right }: any =
    useStyles()({
      workbook,
      aspectRatio,
      zoom: zoomScale * 100,
      isZoomedOut,
      isZoomedIn,
      isTwoPages,
      isPortrait,
      isSafari,
      isIos,
      isFullScreen,
      pageWidth: pageWidth()
    }) as any;

  const onPageLoadSuccess = ({ width, height }) => {
    setAspectRatio(`${width} / ${height}`);
  };

  const handlePageClick = pageNumber => {
    if (!isNumber(pageNumber)) return;

    const { bookCode, edition } = params;

    let currentPageNumber = pageNumber;

    if (!isTwoPages) currentPageNumber = pageNumber % 2 !== 0 ? pageNumber - 1 : pageNumber;

    const url = `/books/${String(bookCode)}/${String(edition)}/${currentPageNumber}${
      isTwoPages ? '/zoom' : ''
    }`;

    navigate(url);
  };

  const isElementInViewport = el => {
    const rect = el.getBoundingClientRect();

    return Math.abs(rect.left) + rect.width < window.innerWidth;
  };

  const scrollToCenter = () => {
    if (!renderedWorkbook || !renderedCanvas?.length) return;
    const twoPageCanvasToUse = isElementInViewport(renderedCanvas[0])
      ? renderedCanvas[0]
      : renderedCanvas[1];

    const canvasToUse = !isTwoPages ? renderedCanvas[0] : twoPageCanvasToUse;
    canvasToUse.scrollIntoView({ block: 'nearest', inline: 'center' });
  };

  useEffect(() => loadPdfWorker(pdfjs.version), []);

  useEffect(() => {
    if (!isDocumentLoaded) return;
    setDocumentHeight(renderedDocument?.clientHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen, renderedDocument?.clientHeight, isTwoPages, isDocumentLoaded]);

  useEffect(() => {
    scrollToCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomScale]);

  const heightMultiplier = isIos && isPortrait ? 0.9 : 1;

  return (
    <Box className={container}>
      <Box
        className={documentContainer}
        style={{
          transform: `scale(${zoomScale})`,
          transformOrigin: 'center'
        }}
      >
        <Document
          // className={document}
          file={workbook.bookUrl}
          loading={getTranslation(CommonTranslationVariant.loading)}
          renderMode="canvas"
          onLoadSuccess={() => setIsDocumentLoaded(true)}
        >
          {!isEmpty(workbook.bookUrl) && (
            <Box id="pageContainer" className={pageContainer}>
              <Box
                className={`${left} ${pageWrapper}`}
                onDoubleClick={() => handlePageClick(currentPage)}
              >
                <Page
                  pageNumber={currentPage + workbook.pageOffset}
                  onLoadSuccess={onPageLoadSuccess}
                  height={documentHeight * heightMultiplier}
                />
              </Box>
              {isTwoPages && (
                <Box
                  className={`${right} ${pageWrapper}`}
                  onDoubleClick={() => handlePageClick(currentPage + 1)}
                >
                  <Page
                    pageNumber={currentPage + 1 + workbook.pageOffset}
                    onLoadSuccess={onPageLoadSuccess}
                    height={documentHeight * heightMultiplier}
                  />
                </Box>
              )}
            </Box>
          )}
        </Document>
      </Box>
    </Box>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    container: (props: any) => ({
      position: 'relative',
      height: '100%',
      width: '100%',
      display: props.isZoomedOut ? 'flex' : 'unset',
      justifyContent: props.isZoomedOut ? 'center' : 'unset',
      alignItems: props.isZoomedOut ? 'center' : 'unset',
      cursor: props.isZoomedIn ? 'inherit' : 'default'
    }),
    document: {
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      perspective: 0
    },
    pageContainer: (props: any) => ({
      height: '95%',
      maxWidth: `${props.zoom - 5}%`,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: props.isIos || (props.isSafari && !props.isPortrait) ? '100%' : 'max-content'
    }),
    documentContainer: (props: any) => ({
      height: `${props.zoom}%`,
      width: `${props.zoom}%`,
      position: 'relative',
      overflow: 'auto',
      display: props.isZoomedOut ? 'flex' : 'unset',
      justifyContent: props.isZoomedOut ? 'center' : 'unset',
      alignItems: props.isZoomedOut ? 'center' : 'unset',
      '& > div:first-of-type': {
        height: `${props.zoom}%`,
        width: `${props.zoom}%`,
        position: 'relative',
        overflow: 'auto',
        minWidth: props.isTwoPages && !props.isPortrait ? 'unset' : `100%`,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }),
    pageWrapper: (props: any) => ({
      height: 'min-content',
      width: 'auto',
      maxHeight: '100%',
      aspectRatio: props.aspectRatio,
      cursor: props.isZoomedIn ? 'inherit' : 'pointer',
      '& > div:first-of-type': {
        height: 'min-content !important',
        maxHeight: 'inherit',
        aspectRatio: props.aspectRatio,
        width: props.isSafari && props.isPortrait ? `${props.zoom - 5}vw` : 'unset',
        '& > canvas:first-of-type': {
          maxHeight: 'inherit',
          maxWidth: '100%'
        },
        '& > div:nth-of-type(1n)': {
          display: 'none'
        }
      }
    }),
    left: (props: any) => ({
      boxShadow: '0 0 25px 3px #888',
      padding: props.isTwoPages ? '0 35px 0 0' : '0',
      background: 'white',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: props.pageWidth
    }),
    right: (props: any) => ({
      boxShadow: '10px 0 25px 3px #888',
      padding: '0 0px 0 35px',
      background: props.isTwoPages ? 'linear-gradient(to left, white 96%, #efeded)' : 'white',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: props.pageWidth
    })
  }));

export default flow(onlyUpdateForKeys(['workbook', 'isTwoPages', 'currentPage', 'fingerprint']))(
  PdfViewer
) as any;
